import { create } from 'zustand';

interface ThemeState {
  primaryColor: string | null;
  setThemeColor: (color: string | null) => void;
}

const FALLBACK_PRIMARY = '#1F2937';

function normalizeToHex(input: string | null): string | null {
  if (!input) return null;
  let v = input.trim();

  if (v.toLowerCase().startsWith('rgb')) {
    const m = v.match(
      /rgba?\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i
    );
    if (!m) return null;
    const toHex = (n: number) =>
      Math.round(Math.max(0, Math.min(255, n)))
        .toString(16)
        .padStart(2, '0');
    v = `#${toHex(Number(m[1]))}${toHex(Number(m[2]))}${toHex(Number(m[3]))}`;
  }

  v = v.replace('#', '');
  if (/^[0-9a-fA-F]{3}$/.test(v)) {
    v = v
      .split('')
      .map(c => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(v)) return null;
  return `#${v.toUpperCase()}`;
}

function generateColorVariations(baseColor: string) {
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const mixWithWhite = (weight: number) => {
    const mr = r * weight + 255 * (1 - weight);
    const mg = g * weight + 255 * (1 - weight);
    const mb = b * weight + 255 * (1 - weight);
    const toHex = (n: number) =>
      Math.round(Math.max(0, Math.min(255, n)))
        .toString(16)
        .padStart(2, '0');
    return `#${toHex(mr)}${toHex(mg)}${toHex(mb)}`;
  };

  return {
    primary: baseColor,
    primaryHover: `rgb(${Math.max(0, r - 20)}, ${Math.max(
      0,
      g - 20
    )}, ${Math.max(0, b - 20)})`,
    primaryLight: mixWithWhite(0.12),
    primaryMedium: mixWithWhite(0.5),
    accent: mixWithWhite(0.06),
    primaryBg: mixWithWhite(0.12),
  };
}

function applyThemeToDOM(color: string) {
  const colors = generateColorVariations(color);
  const root = document.documentElement;

  root.style.setProperty('--primary', colors.primary);
  root.style.setProperty('--ring', colors.primary);
  root.style.setProperty('--accent', colors.accent);
  root.style.setProperty('--primary-bg', colors.primaryBg);
  root.style.setProperty('--theme-primary', colors.primary);
  root.style.setProperty('--theme-primary-hover', colors.primaryHover);
  root.style.setProperty('--theme-primary-light', colors.primaryLight);
  root.style.setProperty('--theme-primary-medium', colors.primaryMedium);
}

export const useThemeStore = create<ThemeState>(set => ({
  primaryColor: null,

  setThemeColor: (color: string | null) => {
    const normalized = normalizeToHex(color);
    const colorToUse = normalized ?? FALLBACK_PRIMARY;
    applyThemeToDOM(colorToUse);
    set({ primaryColor: colorToUse });
  },
}));
